import { useState } from 'react';
import ReactSelect, { MenuPlacement, FormatOptionLabelMeta, components, FilterOptionOption } from 'react-select';
import { useTranslation } from 'react-i18next';

import { CommonGlobalService, PulsePin, defaultSelectStyle } from '@umanforge/common';

interface SelectProp<Option> {
  label?: string;
  placeholder?: string;
  value?: Option;
  icon?: React.SVGProps<SVGSVGElement>;
  options: readonly Option[];
  disabled?: boolean;
  required?: boolean;
  menuPlacement?: MenuPlacement;
  formatOptionLabel?: ( data: Option, formatOptionLabelMeta: FormatOptionLabelMeta<Option> ) => React.ReactNode;
  onChange?: ( newValue: Option ) => void;
};

export function Select<Option>(  { label, placeholder, icon, value, options, menuPlacement, formatOptionLabel, onChange, required = false, disabled = false }: SelectProp<Option> ) {
  const { t } = useTranslation();

  const [ item, setItem ] = useState( value );
  const [ items ] = useState( options ?? [] );

  const [ isFocus, setIsFocus ] = useState<boolean>( false );


  return (
    <div className='flex flex-col gap-[2px] w-full'>
      <p className='ml-1 text-base'>
        {label}
      </p>
      <div className='relative'>
        <div className='min-w-[150px]'>
          <ReactSelect
            value={ item }
            options={ items }
            placeholder={ placeholder }
            formatOptionLabel={ formatOptionLabel }
            menuPlacement={ menuPlacement ?? 'bottom' }
            required={ required }
            isDisabled={ disabled }
            onFocus={ () => { setIsFocus( true ); } }
            onBlur={ () => { setIsFocus( false ); } }
            blurInputOnSelect={ true }
            filterOption={ ( option: FilterOptionOption<Option>, inputValue: string ) => {
              return CommonGlobalService.normalizeString( option.label ).includes( CommonGlobalService.normalizeString( inputValue ) );
            } }
            noOptionsMessage={ () => {
              return (
                <span className="custom-css-class">{ t( 'No element' ) }</span>
              );
            } }
            components={{
              // eslint-disable-next-line react/prop-types
              Control: ( { children, ...rest } ) => {
                return (
                  <components.Control {...rest}>
                    { icon ?
                      <span className='px-2 my-2 select-none border-r'>
                        { icon as JSX.Element }
                      </span>
                      :
                      null
                    }
                    {children}
                  </components.Control>
                ); }
            }}
            styles={{
              control: ( styles, { isFocused, isDisabled } ) => {
                return defaultSelectStyle( isFocused, isDisabled );
              },
              singleValue: ( styles, { isDisabled } ) => {
                return {
                  ...styles,
                  color: ( isDisabled ? '#6b7280' : '#000000' )
                };
              },
              menu: styles => {
                styles.zIndex = 10;
                return styles;
              },
            }}
            onChange={ newValue => {
              setItem( newValue ?? undefined );
              if ( onChange ) {
                onChange( newValue as Option );
              }
            } }
          />
        </div>
        <PulsePin isDisplay={ required && item == null } isPulse={ isFocus }/>
      </div>
    </div>
  );
};